import React, { useState, useMemo, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  useMediaQuery,
  useTheme,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  List,
  ListItemButton,
  ListItemText,
  Drawer,
  Collapse,
  Fade,
  Container,
  Divider,
  Chip,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close,
  Inventory,
  Lightbulb,
  RoomService,
  Info,
  Category,
  Print,
  QrCodeScanner,
  LocalShipping,
  Smartphone,
  Code,
  Tablet,
  Wifi,
  Colorize,
  ListAlt,
  Store,
  Warehouse,
  Factory,
  MedicalInformation,
  AccountBalance,
  LocationCity,
  Inventory2,
  Email,
  People,
  Work,
  Facebook,
  Twitter,
  LinkedIn,
  Instagram,
  Phone,
  LocationOn,
  KeyboardArrowDown,
  PointOfSale,
  SmartScreen,
  YouTube,
  Security,
  SupportAgent,
  ArrowForward,
} from '@mui/icons-material';
import { Link as RouterLink, useLocation } from 'react-router-dom';

const COLORS = {
  primary: '#123465',
  secondary: '#f9a838',
  lightBg: '#f5faff',
  white: '#fff',
  border: 'rgba(18, 52, 101, 0.08)',
  glass: 'rgba(255,255,255,0.92)',
};

export default function Navbar() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState({});
  const [openMenus, setOpenMenus] = useState({});
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  useEffect(() => {
    if (isDesktop && drawerOpen) {
      setDrawerOpen(false);
      setOpenMenus({});
    }
  }, [isDesktop, drawerOpen]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = useMemo(
    () => [
      {
        title: 'Produits',
        icon: <Inventory />,
        children: [
          { title: 'Tous les produits', to: '/produits', icon: <Category />, badge: null },
          { title: "Imprimantes d'étiquettes", to: '/produits/imprimantes', icon: <Print /> },
          { title: 'Scanners de code à barres', to: '/produits/scanners', icon: <QrCodeScanner /> },
          { title: 'Terminaux embarqués', to: '/produits/terminaux-embarques', icon: <LocalShipping /> },
          { title: 'Terminaux mobiles', to: '/produits/terminaux-mobiles', icon: <Smartphone /> },
          { title: 'Tablettes industrielles', to: '/produits/tablettes-industrielles', icon: <Tablet /> },
          { title: 'Terminaux point de vente (TPV)', to: '/produits/terminaux-point-de-vente', icon: <PointOfSale />, badge: 'Nouveau' },
          { title: 'Bornes interactives & kiosques', to: '/produits/bornes-interactives-kiosques', icon: <SmartScreen />, badge: 'Nouveau' },
          { title: 'Logiciels', to: '/produits/logiciels', icon: <Code /> },
          { title: 'Infrastructure WI-FI', to: '/produits/infrastructure-wi-fi', icon: <Wifi /> },
          { title: 'Consommables pour impression', to: '/produits/consommables', icon: <Colorize /> },
        ],
      },
      {
        title: 'Solutions',
        icon: <Lightbulb />,
        children: [
          { title: 'Toutes les solutions', to: '/solutions', icon: <ListAlt /> },
          { title: 'Distribution et points de vente', to: '/solutions/distribution', icon: <Store /> },
          { title: 'Entrepôt et supply chain', to: '/solutions/logistique', icon: <Warehouse /> },
          { title: 'Industrie', to: '/solutions/industrie', icon: <Factory /> },
          { title: 'Santé et pharma', to: '/solutions/sante', icon: <MedicalInformation /> },
          { title: 'Banques et assurances', to: '/solutions/banque', icon: <AccountBalance /> },
          { title: 'Services publics', to: '/solutions/services-publics', icon: <LocationCity /> },
          { title: 'Gestion des inventaires', to: '/solutions/gestion-des-inventaires', icon: <Inventory2 /> },
        ],
      },
      {
        title: 'Services',
        icon: <RoomService />,
        children: [
          { title: 'Tous les services', to: '/services', icon: <ListAlt /> },
          { title: 'Cybersécurité', to: '/services/cybersecurite', icon: <Security />, badge: 'Nouveau' },
          { title: 'Impression managée', to: '/services/impression-managee', icon: <Print /> },
          { title: 'Gestion MDM', to: '/services/gestion-mdm', icon: <Smartphone /> },
          { title: "Développement d'applications", to: '/services/developpement-applications', icon: <Code /> },
          { title: 'Conseil & support IT', to: '/services/conseil-support-it', icon: <SupportAgent /> },
          { title: 'Gestion des inventaires', to: '/services/gestion-des-inventaires', icon: <Inventory2 /> },
        ],
      },
      {
        title: 'À propos',
        icon: <Info />,
        children: [
          { title: 'Contactez-nous', to: '/contactus', icon: <Email /> },
          { title: 'Qui sommes-nous', to: '/a-propos', icon: <People /> },
          { title: 'Carrière', to: '/carriere', icon: <Work /> },
        ],
      },
    ],
    []
  );

  const handleMenuOpen = (event, title) => {
    setAnchorEl((prev) => ({ ...prev, [title]: event.currentTarget }));
  };

  const handleMenuClose = (title) => {
    setAnchorEl((prev) => ({ ...prev, [title]: null }));
  };

  const toggleMobileMenu = (title) => {
    setOpenMenus((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  const handleLinkClick = () => {
    setDrawerOpen(false);
    setOpenMenus({});
    setAnchorEl({});
    window.scrollTo(0, 0);
  };

  const isMenuActive = (item) => {
    if (item.to && location.pathname === item.to) return true;
    if (item.children) {
      return item.children.some((child) => location.pathname === child.to);
    }
    return false;
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: scrolled ? COLORS.glass : COLORS.white,
          color: COLORS.primary,
          transition: 'all 0.35s cubic-bezier(0.4,0,0.2,1)',
          zIndex: 1100,
          borderBottom: `1px solid ${scrolled ? 'rgba(18,52,101,0.06)' : COLORS.border}`,
          backdropFilter: scrolled ? 'blur(20px) saturate(180%)' : 'none',
          WebkitBackdropFilter: scrolled ? 'blur(20px) saturate(180%)' : 'none',
          boxShadow: scrolled
            ? '0 2px 32px rgba(18,52,101,0.08), 0 1px 0 rgba(18,52,101,0.04)'
            : 'none',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar
            disableGutters
            sx={{
              justifyContent: 'space-between',
              py: { xs: 0.75, md: 1 },
              minHeight: { xs: 60, md: 68 },
              gap: 2,
            }}
          >
            {/* Logo */}
            <RouterLink to="/" onClick={handleLinkClick} style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              <img
                src="/images/logo.webp"
                width="86"
                height="36"
                alt="Teledyne Logo"
                onError={(e) => { const image = e.currentTarget; if (!image.src.endsWith('/images/logo2.webp')) image.src = '/images/logo2.webp'; }}
                style={{ transition: 'transform 0.3s ease, opacity 0.3s ease' }}
                onMouseOver={(e) => (e.target.style.transform = 'scale(1.04)')}
                onMouseOut={(e) => (e.target.style.transform = 'scale(1)')}
              />
            </RouterLink>

            {/* Desktop Nav */}
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                gap: { md: 0.25, lg: 0.5 },
                flex: 1,
                justifyContent: 'center',
              }}
            >
              {menuItems.map((item, index) => (
                <Box key={index} sx={{ position: 'relative' }}>
                  {item.children ? (
                    <>
                      <Box
                        component="button"
                        onClick={(e) => handleMenuOpen(e, item.title)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.4,
                          color: isMenuActive(item) ? COLORS.secondary : COLORS.primary,
                          fontSize: '14px',
                          fontWeight: isMenuActive(item) ? 700 : 500,
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          borderRadius: '8px',
                          px: 1.25,
                          py: 0.9,
                          transition: 'all 0.2s ease',
                          position: 'relative',
                          letterSpacing: '0.01em',
                          '&:hover': {
                            color: COLORS.secondary,
                          },
                          '&::after': isMenuActive(item) ? {
                            content: '""',
                            position: 'absolute',
                            bottom: -2,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: '60%',
                            height: 2,
                            borderRadius: 2,
                            bgcolor: COLORS.secondary,
                          } : {},
                        }}
                        aria-controls={`menu-${item.title}`}
                        aria-haspopup="true"
                        aria-expanded={Boolean(anchorEl[item.title])}
                      >
                        {item.title}
                        <KeyboardArrowDown
                          sx={{
                            fontSize: '16px',
                            transform: Boolean(anchorEl[item.title]) ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.25s ease',
                            opacity: 0.7,
                          }}
                        />
                      </Box>
                      <Menu
                        id={`menu-${item.title}`}
                        anchorEl={anchorEl[item.title]}
                        open={Boolean(anchorEl[item.title])}
                        onClose={() => handleMenuClose(item.title)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        TransitionComponent={Fade}
                        PaperProps={{
                          sx: {
                            mt: 1.5,
                            minWidth: 290,
                            bgcolor: COLORS.white,
                            boxShadow: '0 20px 60px rgba(18,52,101,0.12), 0 4px 16px rgba(18,52,101,0.06)',
                            borderRadius: '16px',
                            border: `1px solid rgba(18,52,101,0.07)`,
                            p: 1,
                            overflow: 'visible',
                            '&:before': {
                              content: '""',
                              display: 'block',
                              position: 'absolute',
                              top: -6,
                              left: 28,
                              width: 12,
                              height: 12,
                              bgcolor: COLORS.white,
                              border: `1px solid rgba(18,52,101,0.07)`,
                              borderBottom: 'none',
                              borderRight: 'none',
                              transform: 'rotate(45deg)',
                              zIndex: 0,
                            },
                          },
                        }}
                      >
                        {item.children.map((child, idx) => {
                          const isActive = location.pathname === child.to;
                          return (
                            <MenuItem
                              key={idx}
                              component={RouterLink}
                              to={child.to}
                              onClick={() => {
                                handleMenuClose(item.title);
                                handleLinkClick();
                              }}
                              sx={{
                                color: isActive ? COLORS.secondary : COLORS.primary,
                                fontSize: '13.5px',
                                fontWeight: isActive ? 700 : 500,
                                py: 0.9,
                                px: 1.25,
                                borderRadius: '10px',
                                mb: 0.2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                transition: 'all 0.18s ease',
                                bgcolor: isActive ? `rgba(249,168,56,0.08)` : 'transparent',
                                '&:hover': {
                                  bgcolor: `rgba(18,52,101,0.04)`,
                                  color: COLORS.primary,
                                  transform: 'translateX(3px)',
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 30,
                                  height: 30,
                                  borderRadius: '8px',
                                  bgcolor: isActive ? `rgba(249,168,56,0.12)` : `rgba(18,52,101,0.05)`,
                                  color: isActive ? COLORS.secondary : COLORS.primary,
                                  flexShrink: 0,
                                  '& svg': { fontSize: '15px' },
                                }}
                              >
                                {child.icon}
                              </Box>
                              <Typography sx={{ fontSize: '13.5px', lineHeight: 1.4, flex: 1 }}>
                                {child.title}
                              </Typography>
                              {child.badge && (
                                <Chip
                                  label={child.badge}
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: '10px',
                                    bgcolor: COLORS.secondary,
                                    color: COLORS.primary,
                                    fontWeight: 700,
                                    '& .MuiChip-label': { px: 0.75 },
                                  }}
                                />
                              )}
                            </MenuItem>
                          );
                        })}
                      </Menu>
                    </>
                  ) : (
                    <Typography
                      component={RouterLink}
                      to={item.to}
                      onClick={handleLinkClick}
                      sx={{
                        color: isMenuActive(item) ? COLORS.secondary : COLORS.primary,
                        fontSize: '14px',
                        fontWeight: isMenuActive(item) ? 700 : 500,
                        textDecoration: 'none',
                        borderRadius: '8px',
                        px: 1.25,
                        py: 0.9,
                        display: 'flex',
                        alignItems: 'center',
                        transition: 'all 0.2s ease',
                        letterSpacing: '0.01em',
                        '&:hover': {
                          color: COLORS.secondary,
                        },
                      }}
                    >
                      {item.title}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>

            {/* Desktop CTA */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
              <Button component="a" href="https://store.teledyne.ma/" target="_blank" rel="noopener noreferrer"
                aria-label="Boutique (nouvel onglet)" variant="outlined" startIcon={<Store />}
                sx={{ color: COLORS.primary, borderColor: COLORS.primary, borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}>
                Boutique
              </Button>
              <Button
                component={RouterLink}
                to="/contactus"
                onClick={handleLinkClick}
                variant="contained"
                endIcon={<ArrowForward sx={{ fontSize: '14px !important' }} />}
                sx={{
                  bgcolor: COLORS.primary,
                  color: COLORS.white,
                  fontWeight: 600,
                  fontSize: '13.5px',
                  borderRadius: '10px',
                  px: 2.25,
                  py: 0.9,
                  textTransform: 'none',
                  letterSpacing: '0.01em',
                  boxShadow: 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: COLORS.secondary,
                    color: COLORS.primary,
                    boxShadow: '0 4px 16px rgba(249,168,56,0.35)',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                Contactez-nous
              </Button>
            </Box>

            {/* Mobile Hamburger */}
            <IconButton
              onClick={() => setDrawerOpen(true)}
              aria-label="Ouvrir le menu mobile"
              sx={{
                display: { xs: 'flex', md: 'none' },
                color: COLORS.primary,
                border: `1.5px solid ${COLORS.border}`,
                borderRadius: '10px',
                width: 42,
                height: 40,
                transition: 'all 0.2s ease',
                '&:hover': {
                  color: COLORS.white,
                  bgcolor: COLORS.primary,
                  borderColor: COLORS.primary,
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        open={drawerOpen}
        onClose={handleLinkClick}
        anchor="left"
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '85vw', sm: 340 },
            bgcolor: COLORS.white,
            height: '100%',
            zIndex: 1200,
            boxSizing: 'border-box',
            boxShadow: '8px 0 40px rgba(18,52,101,0.12)',
            overflowY: 'auto',
          },
          '& .MuiBackdrop-root': {
            bgcolor: 'rgba(18,52,101,0.3)',
            backdropFilter: 'blur(4px)',
          },
        }}
      >
        <Fade in={drawerOpen} timeout={250}>
          <Box role="navigation" aria-label="Menu de navigation mobile">
            {/* Drawer Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2.5,
                py: 2,
                borderBottom: `1px solid ${COLORS.border}`,
                position: 'sticky',
                top: 0,
                bgcolor: COLORS.white,
                zIndex: 10,
              }}
            >
              <RouterLink to="/" onClick={handleLinkClick}>
                <img
                  src="/images/logo.webp"
                  height="30"
                  alt="Teledyne Logo"
                  onError={(e) => { const image = e.currentTarget; if (!image.src.endsWith('/images/logo2.webp')) image.src = '/images/logo2.webp'; }}
                />
              </RouterLink>
              <IconButton
                onClick={handleLinkClick}
                aria-label="Fermer le menu"
                size="small"
                sx={{
                  color: COLORS.primary,
                  bgcolor: `rgba(18,52,101,0.05)`,
                  borderRadius: '8px',
                  width: 36,
                  height: 36,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: COLORS.primary,
                    color: COLORS.white,
                    transform: 'rotate(90deg)',
                  },
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>

            {/* Nav Items */}
            <List sx={{ px: 1.5, pt: 1.5, pb: 0 }}>
              {menuItems.map((item, index) => (
                <Box key={index} sx={{ mb: 0.5 }}>
                  {item.children ? (
                    <>
                      <ListItemButton
                        onClick={() => toggleMobileMenu(item.title)}
                        sx={{
                          py: 1.25,
                          px: 1.5,
                          borderRadius: '10px',
                          transition: 'all 0.2s ease',
                          bgcolor: isMenuActive(item) ? `rgba(249,168,56,0.08)` : 'transparent',
                          '&:hover': {
                            bgcolor: `rgba(18,52,101,0.04)`,
                          },
                          ...(isMenuActive(item) && { color: COLORS.secondary }),
                        }}
                        aria-expanded={!!openMenus[item.title]}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 34,
                            height: 34,
                            borderRadius: '8px',
                            bgcolor: isMenuActive(item) ? `rgba(249,168,56,0.12)` : `rgba(18,52,101,0.05)`,
                            color: isMenuActive(item) ? COLORS.secondary : COLORS.primary,
                            mr: 1.5,
                            flexShrink: 0,
                            '& svg': { fontSize: '18px' },
                          }}
                        >
                          {item.icon}
                        </Box>
                        <ListItemText
                          primary={item.title}
                          primaryTypographyProps={{
                            fontWeight: isMenuActive(item) ? 700 : 600,
                            fontSize: '14.5px',
                            color: isMenuActive(item) ? COLORS.secondary : COLORS.primary,
                          }}
                        />
                        <Box
                          sx={{
                            transition: 'transform 0.25s ease',
                            transform: openMenus[item.title] ? 'rotate(180deg)' : 'rotate(0deg)',
                            color: COLORS.primary,
                            display: 'flex',
                            opacity: 0.5,
                          }}
                        >
                          <KeyboardArrowDown fontSize="small" />
                        </Box>
                      </ListItemButton>
                      <Collapse in={openMenus[item.title]} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding sx={{ pl: 1, pt: 0.5, pb: 0.5 }}>
                          {item.children.map((child, idx) => {
                            const isActive = location.pathname === child.to;
                            return (
                              <ListItemButton
                                key={idx}
                                component={RouterLink}
                                to={child.to}
                                onClick={handleLinkClick}
                                sx={{
                                  py: 1,
                                  px: 1.5,
                                  borderRadius: '8px',
                                  mb: 0.25,
                                  transition: 'all 0.18s ease',
                                  bgcolor: isActive ? `rgba(249,168,56,0.08)` : 'transparent',
                                  color: isActive ? COLORS.secondary : COLORS.primary,
                                  '&:hover': {
                                    bgcolor: `rgba(18,52,101,0.04)`,
                                    transform: 'translateX(4px)',
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 28,
                                    height: 28,
                                    borderRadius: '6px',
                                    bgcolor: isActive ? `rgba(249,168,56,0.12)` : `rgba(18,52,101,0.05)`,
                                    color: isActive ? COLORS.secondary : COLORS.primary,
                                    mr: 1.5,
                                    flexShrink: 0,
                                    '& svg': { fontSize: '14px' },
                                  }}
                                >
                                  {child.icon}
                                </Box>
                                <ListItemText
                                  primary={child.title}
                                  primaryTypographyProps={{
                                    fontSize: '13.5px',
                                    fontWeight: isActive ? 700 : 500,
                                    color: isActive ? COLORS.secondary : COLORS.primary,
                                  }}
                                />
                                {child.badge && (
                                  <Chip
                                    label={child.badge}
                                    size="small"
                                    sx={{
                                      height: 18,
                                      fontSize: '10px',
                                      bgcolor: COLORS.secondary,
                                      color: COLORS.primary,
                                      fontWeight: 700,
                                      '& .MuiChip-label': { px: 0.75 },
                                    }}
                                  />
                                )}
                              </ListItemButton>
                            );
                          })}
                        </List>
                      </Collapse>
                    </>
                  ) : (
                    <ListItemButton
                      component={RouterLink}
                      to={item.to}
                      onClick={handleLinkClick}
                      sx={{
                        py: 1.25,
                        px: 1.5,
                        borderRadius: '10px',
                        transition: 'all 0.2s ease',
                        bgcolor: isMenuActive(item) ? `rgba(249,168,56,0.08)` : 'transparent',
                        color: isMenuActive(item) ? COLORS.secondary : COLORS.primary,
                        '&:hover': {
                          bgcolor: `rgba(18,52,101,0.04)`,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 34,
                          height: 34,
                          borderRadius: '8px',
                          bgcolor: isMenuActive(item) ? `rgba(249,168,56,0.12)` : `rgba(18,52,101,0.05)`,
                          color: isMenuActive(item) ? COLORS.secondary : COLORS.primary,
                          mr: 1.5,
                          flexShrink: 0,
                          '& svg': { fontSize: '18px' },
                        }}
                      >
                        {item.icon}
                      </Box>
                      <ListItemText
                        primary={item.title}
                        primaryTypographyProps={{
                          fontWeight: isMenuActive(item) ? 700 : 600,
                          fontSize: '14.5px',
                          color: isMenuActive(item) ? COLORS.secondary : COLORS.primary,
                        }}
                      />
                    </ListItemButton>
                  )}
                </Box>
              ))}
            </List>

            {/* Mobile CTA */}
            <Box sx={{ px: 2, pt: 1, pb: 1 }}>
              <Button component="a" href="https://store.teledyne.ma/" target="_blank" rel="noopener noreferrer"
                aria-label="Boutique (nouvel onglet)" onClick={handleLinkClick} variant="outlined" fullWidth startIcon={<Store />}
                sx={{ mb: 2, color: COLORS.primary, borderColor: COLORS.primary, borderRadius: '10px', py: 1.25, textTransform: 'none', fontWeight: 600 }}>
                Boutique
              </Button>
              <Button
                component={RouterLink}
                to="/contactus"
                onClick={handleLinkClick}
                variant="contained"
                fullWidth
                endIcon={<ArrowForward />}
                sx={{
                  bgcolor: COLORS.primary,
                  color: COLORS.white,
                  fontWeight: 600,
                  fontSize: '14px',
                  borderRadius: '10px',
                  py: 1.25,
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: COLORS.secondary,
                    color: COLORS.primary,
                  },
                }}
              >
                Contactez-nous
              </Button>
            </Box>

            <Divider sx={{ mx: 2, my: 1.5, borderColor: COLORS.border }} />

            {/* Social Links */}
            <Box sx={{ px: 2.5, mb: 2 }}>
              <Typography variant="caption" sx={{ color: COLORS.primary, fontWeight: 700, opacity: 0.4, textTransform: 'uppercase', letterSpacing: 1.2, fontSize: '10px' }}>
                Suivez-nous
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                {[
                  { href: 'https://www.facebook.com/webteledyne', icon: <Facebook fontSize="small" />, label: 'Facebook' },
                  { href: 'https://twitter.com/Teledyne9', icon: <Twitter fontSize="small" />, label: 'Twitter' },
                  { href: 'https://www.linkedin.com/company/teledyne', icon: <LinkedIn fontSize="small" />, label: 'LinkedIn' },
                  { href: 'https://www.instagram.com/teledyne_maroc/', icon: <Instagram fontSize="small" />, label: 'Instagram' },
                  { href: 'https://www.youtube.com/@nizarbenslimane/', icon: <YouTube fontSize="small" />, label: 'YouTube' },
                ].map((s) => (
                  <IconButton
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={s.label}
                    size="small"
                    sx={{
                      color: COLORS.primary,
                      bgcolor: `rgba(18,52,101,0.05)`,
                      borderRadius: '8px',
                      width: 36,
                      height: 36,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: COLORS.primary,
                        color: COLORS.white,
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    {s.icon}
                  </IconButton>
                ))}
              </Box>
            </Box>

            {/* Contact Info */}
            <Box sx={{ px: 2.5, pb: 3 }}>
              <Typography variant="caption" sx={{ color: COLORS.primary, fontWeight: 700, opacity: 0.4, textTransform: 'uppercase', letterSpacing: 1.2, fontSize: '10px' }}>
                Contact
              </Typography>
              <Box
                sx={{
                  mt: 1.5,
                  p: 2,
                  borderRadius: '12px',
                  bgcolor: `rgba(18,52,101,0.03)`,
                  border: `1px solid ${COLORS.border}`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.25,
                }}
              >
                {[
                  { icon: <Email sx={{ fontSize: '14px' }} />, text: 'contact@teledyne.ma' },
                  { icon: <Phone sx={{ fontSize: '14px' }} />, text: '+212 522 307 664' },
                  { icon: <LocationOn sx={{ fontSize: '14px' }} />, text: '2, rue Abdelouhab Azzakkak, Casablanca' },
                ].map((c, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, color: COLORS.primary }}>
                    <Box sx={{ mt: 0.2, opacity: 0.5, flexShrink: 0 }}>{c.icon}</Box>
                    <Typography variant="body2" sx={{ fontSize: '12.5px', lineHeight: 1.5, color: COLORS.primary, opacity: 0.8 }}>{c.text}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Fade>
      </Drawer>
    </>
  );
}
